import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { DepartmentSummary, Teacher } from '@edtech-ui/core/models/admin-panel/admin-panel-models';
import { TeacherManagementService } from '@edtech-ui/core/services/admin-panel/teacherManagement.service';
import { MatDialog } from '@angular/material/dialog';
import { TeacherInviteDialogComponent } from '../teacher-invite-dialog/teacher-invite-dialog.component';
import { BulkUploadDialogComponent } from '../bulk-upload-dialog/bulk-upload-dialog.component';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { AdminHeaderComponent } from '../admin-header/admin-header.component';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { PaginatorModule } from 'primeng/paginator';

@Component({
  selector: 'app-teacher-management-landing',
  standalone: true,
  imports: [
    CommonModule,
    MatTabsModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatFormFieldModule,
    TableModule,
    TagModule,
    ButtonModule,
    InputTextModule,
    FormsModule, AdminHeaderComponent,
    PaginatorModule
  ],
  templateUrl: './teacher-management-landing.component.html',
  styleUrls: ['./teacher-management-landing.component.scss']
})
export class TeacherManagementLandingComponent implements OnInit {
  private destroy$ = new Subject<void>();
  selectedBatch = 'All Batches';
  departmentId: string | number = 'All Departments';
  searchText = '';
  adminEmail = '';
  activeTab: 'active' | 'pending_invites' | 'by_department' = 'active';

  teachers: Teacher[] = [];
  activeTeachers: Teacher[] = [];
  pendingTeachers: Teacher[] = [];
  departmentSummary: DepartmentSummary[] = [];
  departments: { id: number | string; name: string }[] = [];
  isLoading = false;
  searchSubject = new Subject<string>();
  tenantName = 'Teacher Management';
  tenantSubName = 'Manage teachers and invitations';
  page = 1;
  limit = 10;
  totalRecords = 0;

  constructor(private teacherService: TeacherManagementService, private dialog: MatDialog,
    private toastr: ToastrService,
  ) { }

  ngOnInit() {
    this.fetchDepartments();
    this.searchSubject
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(search => {
        this.page = 1;

        if (search.trim().length > 0) {
          this.loadTeachers(this.activeTab, search);
          return;
        }

        this.loadTeachers(this.activeTab, this.searchText);
      });

  }

  onDepartmentChange() {
    this.loadTeachers(this.activeTab);
  }

  onTabChange(event: any) {
    const index = event.index;
    if (index === 0) this.activeTab = 'active';
    else if (index === 1) this.activeTab = 'pending_invites';
    else if (index === 2) this.activeTab = 'by_department';
    this.loadTeachers(this.activeTab);
  }

  loadTeachers(filterType: 'active' | 'pending_invites' | 'by_department', search: string = '') {
    const deptId =
      this.departmentId === 'All Departments' || this.departmentId === 0
        ? undefined
        : this.departmentId;

    this.isLoading = true;

    this.teacherService.getTeachersByFilter(filterType, deptId, this.page, this.limit, search)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.isLoading = false;

          if (!res.Success || !res.Data) {
            this.toastr.warning(res.Message || 'No data found.');
            return;
          }
          if (filterType === 'active' || filterType === 'pending_invites') {
            const data: any = res.Data;
            this.page = data.page;
            this.limit = data.limit;
            this.totalRecords = data.total_pages * data.limit;

            const list =
              filterType === 'active'
                ? (data.teachers_list ?? [])
                : (data.invites_list ?? []);

            const mapped: Teacher[] = list.map((t: any) => {
              const dateField =
                filterType === 'pending_invites'
                  ? t.invited_on || t.created_at
                  : t.joined_on;

              return {
                id: t.id || t.user_id || t.invite_id || 0,
                user_id: t.user_id ?? t.id ?? null,
                invite_id: t.invite_id ?? null,
                name: t.name || '—',
                email: t.email || '—',
                department: t.department_name || t.department || '—',
                role: t.designation || '—',
                status: filterType === 'pending_invites' ? 'Pending' : 'Active',
                joined_on: dateField,
                joined: dateField
                  ? new Date(dateField).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })
                  : '—'
              } as Teacher;
            });

            if (filterType === 'active') this.activeTeachers = mapped;
            else this.pendingTeachers = mapped;

            return;
          }

          this.departmentSummary = (res.Data as any[]).map((d) => ({
            name: d.department_name,
            totalTeachers: d.total_teachers_count,
            activeTeachers: d.active_teachers_count,
            pendingInvites: d.pending_invite_count
          }));
        },

        error: () => {
          this.isLoading = false;
          this.toastr.error('Something went wrong while loading teachers.');
        }
      });
  }

  fetchDepartments() {
    this.teacherService.getDepartments().pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res.Success && res.Data?.length) {
            this.departments = [
              { id: 'All Departments', name: 'All Departments' },
              ...res.Data,
            ];
            this.departmentId = 'All Departments';
            this.loadTeachers(this.activeTab);
          } else {
            this.toastr.error(res.Message || 'Failed to load departments.');
          }
        },
        error: (err) => {
          this.toastr.error('Something went wrong while fetching departments.');
        },
      });
  }
  onPageChange(e: any) {
    this.page = e.page + 1;
    this.limit = e.rows;
    this.loadTeachers(this.activeTab);
  }

  onBulkUpload() {
    this.dialog.open(BulkUploadDialogComponent, {
      data: { type: 'teacher' },
      width: '520px',
      maxWidth: '95vw',
      panelClass: 'custom-dialog-container',
      disableClose: true
    });
  }

  onInviteTeacher() {
    this.dialog.open(TeacherInviteDialogComponent, {
      panelClass: 'custom-dialog-container',
      disableClose: true
    });
  }

  deleteTeacher(teacher: Teacher) {
    const isPending = this.activeTab === 'pending_invites';
    const identifier = isPending
      ? teacher.invite_id || teacher.id
      : teacher.user_id || teacher.id;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '32rem',
      maxWidth: '95vw',
      panelClass: 'custom-dialog-container',
      data: {
        title: isPending ? 'Revoke Invitation?' : 'Delete Teacher?',
        message: isPending
          ? `This will permanently revoke the invitation for ${teacher.name}.`
          : `This will permanently delete ${teacher.name}. This action cannot be undone.`,
        confirmText: isPending ? 'Revoke' : 'Delete',
        cancelText: 'Cancel'
      }
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((confirmed) => {
      if (confirmed && identifier) {
        const deleteCall = isPending
          ? this.teacherService.deletePendingInvite(identifier.toString())
          : this.teacherService.deleteActiveUser(identifier.toString());

        deleteCall.pipe(takeUntil(this.destroy$)).subscribe({
          next: (res) => {
            if (res.Success) {
              this.toastr.success(
                isPending
                  ? `Invitation for ${teacher.name} revoked successfully.`
                  : `${teacher.name} deleted successfully.`
              );
              this.loadTeachers(this.activeTab);
            } else {
              this.toastr.warning(res.Message || 'Operation failed.');
            }
          },
          error: (err) => {
            this.toastr.error('Something went wrong while deleting.');
          }
        });
      } else if (!identifier) {
        this.toastr.error('No valid identifier found for this teacher.');
      }
    });
  }


  onSearchChange(value: string) {
    this.searchSubject.next(value);
  }

  // searchTeachers(query: string) {
  //   this.isLoading = true;
  //   this.teacherService.searchTeachers(query).subscribe({
  //     next: (res) => {
  //       this.isLoading = false;

  //       const teachers = res.Data?.teachers || [];

  //       if (res.Success && teachers.length > 0) {
  //         const mapped: Teacher[] = teachers.map((t: any) => ({
  //           id: t.user_id ?? t.id ?? t.teacher_id,
  //           name: t.name || '—',
  //           email: t.email || '—',
  //           department: t.department || '—',
  //           role: t.role || '—',
  //           status: t.status || 'Active',
  //           joined_on: t.join_date || null,
  //           joined: t.join_date
  //             ? new Date(t.join_date).toLocaleDateString('en-IN', {
  //               year: 'numeric',
  //               month: 'short',
  //               day: 'numeric',
  //             })
  //             : '—',
  //         }));

  //         if (this.activeTab === 'active') this.activeTeachers = mapped;
  //         else if (this.activeTab === 'pending_invites') this.pendingTeachers = mapped;
  //       } else {
  //         this.toastr.info('No teachers found for this search.');
  //       }
  //     },
  //     error: (err) => {
  //       this.isLoading = false;
  //       this.toastr.error('Error while searching teachers.');
  //     },
  //   });
  // }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}